import { TranslateService } from '@ngx-translate/core';
import { DeviceAndBrowserDetectorService } from 'src/app/services/device-and-browser-detector.service';
import { DeviceErrorType, DeviceState } from 'src/app/models/device-manger';
import { DeviceType } from '../devices-handle-util';
import {
  Browser,
  DeviceCategoryName,
  DeviceErrorHelperModelBase,
  DeviceErrorHelperStep,
} from './device-error-helper-model-base';
import { PermissionHelper } from './permission-helper';

export class BlockByOSHelper extends DeviceErrorHelperModelBase {
  private _permissionHelper: PermissionHelper;
  private _steps: DeviceErrorHelperStep[] = [];
  protected helperSupportedBrowsers: string[] = [
    Browser.CHROME,
    Browser.EDGE,
    Browser.SAFARI,
    Browser.FIREFOX,
    Browser.OPERA,
  ];

  constructor(
    protected deviceDetector: DeviceAndBrowserDetectorService,
    protected translateService: TranslateService,
    protected deviceType: DeviceType,
  ) {
    super(translateService, deviceDetector, deviceType);
    this._permissionHelper = new PermissionHelper(deviceDetector, translateService, deviceType);
  }

  getSteps(): DeviceErrorHelperStep[] {
    this._steps = [];
    if (!this.isSupportedOS()) {
      return this._permissionHelper.getSteps();
    }
    this.buildHelperFlow();
    return this._steps;
  }

  getMobileTitle(): string {
    if (!this.isSupportedOS()) {
      return this._permissionHelper.getMobileTitle();
    }

    let deviceName = '';
    if (this.deviceDetector.isiOSiPhone()) {
      deviceName = DeviceCategoryName.iOS;
    } else if (this.deviceDetector.isiOSiPad()) {
      deviceName = DeviceCategoryName.iPadOS;
    } else if (this.deviceDetector.isAndroid()) {
      deviceName = DeviceCategoryName.Android;
    } else if (this.deviceDetector.isAndroidTablet()) {
      deviceName = DeviceCategoryName.Tablet;
    }

    return this.translateService.instant('Allow camera / mic access on {{device}}', {
      device: deviceName,
    });
  }

  getDesktopTitle(): string {
    if (!this.isSupportedOS()) {
      return this._permissionHelper.getDesktopTitle();
    }

    const browserName = this.BrowserDisplayName();
    let deviceName = '';
    if (this.deviceDetector.isWindows()) {
      deviceName = DeviceCategoryName.Windows;
    } else if (this.deviceDetector.isMacOS()) {
      deviceName = DeviceCategoryName.MacOS;
    }

    return this.translateService.instant('Allow {{browser}} to access camera / mic on {{device}}', {
      browser: browserName,
      device: deviceName,
    });
  }

  hasRelatedError(cam: DeviceState, mic: DeviceState) {
    return (
      this.hasErrorOfType(cam, DeviceErrorType.PERMISSION_DENIED_BY_SYSTEM) ||
      this.hasErrorOfType(mic, DeviceErrorType.PERMISSION_DENIED_BY_SYSTEM)
    );
  }

  showRefreshButton(): boolean {
    return false;
  }

  getErrorMsg(cam: DeviceState, mic: DeviceState): string {
    const camError = this.hasErrorOfType(cam, DeviceErrorType.PERMISSION_DENIED_BY_SYSTEM);
    const micError = this.hasErrorOfType(mic, DeviceErrorType.PERMISSION_DENIED_BY_SYSTEM);

    if (camError && micError) {
      return this.translateService.instant('Access not yet granted');
    } else if (camError) {
      return this.translateService.instant('Cam access not yet granted');
    } else if (micError) {
      return this.translateService.instant('Mic access not yet granted');
    } else {
      return '';
    }
  }

  deviceSettingButtonSrc(): string {
    if (this.deviceDetector.isMacOS()) {
      return `x-apple.systempreferences:com.apple.preference.security?${
        this.deviceType === DeviceType.VIDEO ? 'Privacy_Camera' : 'Privacy_Microphone'
      }`;
    }
    if (this.deviceDetector.isWindows()) {
      return `ms-settings:privacy-${
        this.deviceType === DeviceType.VIDEO ? 'webcam' : 'microphone'
      }`;
    }
    return '';
  }

  protected showRealDeviceSettingsButtonByGuide() {
    return true;
  }

  // in case not supported we
  // will fall back to the permissions helper
  private isSupportedOS() {
    return (
      this.deviceDetector.isAndroid() ||
      this.deviceDetector.isWindows() ||
      this.deviceDetector.isMacOS() ||
      this.deviceDetector.isiOS()
    );
  }

  private buildHelperFlow() {
    if (this.deviceDetector.isMacOS()) {
      this.buildMacOSSteps();
    } else if (this.deviceDetector.isWindows()) {
      this.buildWindowsSteps();
    } else if (this.deviceDetector.isAndroid()) {
      this.buildAndroidSteps();
    } else {
      this.buildiOSSteps();
    }
  }

  private buildMacOSSteps() {
    const browserName = this.BrowserDisplayName();
    const browserImgName = this.getBrowserImgName(browserName);

    this._steps.push(
      {
        stepText:
          `${this.translateService.instant('Open')} ${this.strongHtml(
            this.translateService.instant('System Settings'),
          )} &gt; ` +
          `${this.externalHelpLinkHtml(
            this.deviceSettingButtonSrc(),
            this.translateService.instant('Privacy & Security'),
          )}`,
      },
      {
        stepText: `${this.translateService.instant('Select')} ${this.strongHtml(
          this.translateService.instant('Microphone'),
        )} ${this.translateService.instant('or')} ${this.strongHtml(
          this.translateService.instant('Camera'),
        )}`,
        stepImgSrc: 'helper-modal-guides/blocked-by-os/macOS/step-2.png',
      },
      {
        stepText: `${this.translateService.instant('Find')} ${this.strongHtml(
          browserName,
        )} ${this.translateService.instant('and enable access for both camera and microphone')}`,
        stepImgSrc: `helper-modal-guides/browsers/${browserImgName}`,
      },
    );
  }

  private buildWindowsSteps() {
    const browserName = this.BrowserDisplayName();
    const browserImgName = this.getBrowserImgName(browserName);

    this._steps.push(
      {
        stepText:
          `${this.translateService.instant('Open')} ${this.strongHtml(
            this.translateService.instant('Settings'),
          )} &gt; ` +
          `${this.externalHelpLinkHtml(
            this.deviceSettingButtonSrc(),
            this.translateService.instant('Privacy & Security'),
          )}`,
      },
      {
        stepText: `${this.translateService.instant('Select')} ${this.strongHtml(
          this.translateService.instant('Camera'),
        )} ${this.translateService.instant('or')} ${this.strongHtml(
          this.translateService.instant('Microphone'),
        )}`,
        stepImgSrc: 'helper-modal-guides/blocked-by-os/windows/step-2.png',
      },
      {
        stepText: `${this.translateService.instant('Find')} ${this.strongHtml(
          browserName,
        )} ${this.translateService.instant('and enable access for both camera and microphone')}`,
        stepImgSrc: `helper-modal-guides/browsers/${browserImgName}`,
      },
    );
  }

  private buildAndroidSteps() {
    const browserName = this.BrowserDisplayName();
    this._steps.push(
      {
        stepText:
          `${this.translateService.instant('Open')} ${this.strongHtml(
            this.translateService.instant('Settings'),
          )} &gt; ` +
          `${this.strongHtml(
            this.translateService.instant('Apps'),
          )} ${this.translateService.instant(
            'and then tap on your browser (e.g.,',
          )} ${this.strongHtml(browserName)} ${')'}`,
        stepImgSrc: 'helper-modal-guides/blocked-by-os/android/step-1.png',
      },
      {
        stepText: `${this.translateService.instant('Select')} ${this.strongHtml(
          this.translateService.instant('App permissions'),
        )}`,
        stepImgSrc: 'helper-modal-guides/blocked-by-os/android/step-2.png',
      },
      {
        stepText: `${this.translateService.instant('Select')} ${this.strongHtml(
          this.translateService.instant('Camera'),
        )} ${this.translateService.instant('or')} ${this.strongHtml(
          this.translateService.instant('Microphone'),
        )}`,
        stepImgSrc: 'helper-modal-guides/blocked-by-os/android/step-3.png',
      },
      {
        stepText: `${this.strongHtml(
          this.translateService.instant('Allow'),
        )} ${this.translateService.instant('access for both camera and mic')}`,
        stepImgSrc: 'helper-modal-guides/blocked-by-os/android/step-4.png',
      },
    );
  }

  private buildiOSSteps() {
    const browserName = this.BrowserDisplayName();
    const browserImgName = this.getBrowserImgName(browserName);

    this._steps.push(
      {
        stepText:
          `${this.translateService.instant('Open')} ${this.strongHtml(
            this.translateService.instant('Settings'),
          )} &gt; ` + `${this.strongHtml(this.translateService.instant('Privacy & Security'))}`,
      },
      {
        stepText: `${this.translateService.instant('Select')} ${this.strongHtml(
          this.translateService.instant('Camera'),
        )} ${this.translateService.instant('or')} ${this.strongHtml(
          this.translateService.instant('Microphone'),
        )}`,
        stepImgSrc: 'helper-modal-guides/blocked-by-os/iOS/step-2.png',
      },
      {
        stepText: `${this.translateService.instant('Find your browser (e.g.,')} ${this.strongHtml(
          browserName,
        )} ${')'} ${this.translateService.instant(
          'and enable access for both camera and microphone',
        )}`,
        stepImgSrc: `helper-modal-guides/browsers/${browserImgName}`,
      },
    );
  }
}
